import React, { useState } from "react";
import { Box, Button, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { COLORS } from "./constants";
import { commonInputProps, ghostButtonSx, primaryButtonSx, proseInputSx } from "./styles";
import { platformIcon } from "./utils";
import { ConfirmDialog } from "./ConfirmDialog";

// ─────────────────────────────────────────────────────────────────────────────
// ConnectPanel — a connected YouTube channel / Facebook Page
//
// Everything on this card saves the moment it changes (connect, the on/off
// switch, disconnect, title on blur): it talks to /api/connect directly, not
// through the editor's Save button. The card says so, because the editor's
// "Unsaved changes" bar would otherwise suggest it covers these too.
//
// `blockedReason`: connecting leaves the page for the platform's sign-in, which
// would throw away stream keys typed but not yet saved. While there are any,
// Connect / Switch are disabled and the tooltip says why.
// ─────────────────────────────────────────────────────────────────────────────

const TINT = {
  youtube:  { bg: "rgba(239,68,68,0.18)",  fg: "#FF8A8A" },
  facebook: { bg: "rgba(24,119,242,0.20)", fg: "#8AB8FF" }
};

const labelSx = { display: "block", mb: 0.75, fontSize: 13, fontWeight: 600, color: COLORS.textSoft };

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.cyan },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: COLORS.accent, opacity: 1 },
  "& .MuiSwitch-track": { bgcolor: COLORS.borderStrong, opacity: 1 }
};

const smallBtn = { height: 36, fontSize: 13, px: 1.75 };

function Blockable({ reason, children }) {
  if (!reason) return children;
  // Disabled buttons don't fire mouse events; the span carries the tooltip.
  return <Tooltip title={reason}><span>{children}</span></Tooltip>;
}

export function ConnectPanel({
  platform, platformConnections, connectBusy,
  connectPlatform, disconnectPlatform, toggleConnection, saveMetadata,
  blockedReason
}) {
  const [confirmOff, setConfirmOff] = useState(false);
  const conn   = platformConnections?.[platform];
  const isYt   = platform === "youtube";
  const label  = isYt ? "YouTube" : "Facebook";
  const thing  = isYt ? "channel" : "Page";
  const name   = conn ? (isYt ? conn.channelTitle : conn.pageName) || conn.selectedName || label : "";
  const busy   = connectBusy === platform;
  const tint   = TINT[platform];
  const missing = Array.isArray(conn?.missingPermissions) ? conn.missingPermissions : [];

  const cardSx = {
    p: { xs: 2, sm: 2.5 }, borderRadius: "16px", bgcolor: "#211F4E",
    border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 2
  };

  if (!conn) {
    return (
      <Box sx={cardSx}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.75 }}>
          {platformIcon(platform, 44)}
          <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>
              Connect your {label} {thing}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 13, lineHeight: 1.5, color: COLORS.muted }}>
              Sign in once and Fennira creates the live {isYt ? "broadcast" : "video"} for you each time.
              No key to copy.
            </Typography>
          </Box>
          <Blockable reason={blockedReason}>
            <Button onClick={() => connectPlatform(platform)} disabled={busy || !!blockedReason}
              startIcon={<LinkIcon />}
              sx={{ ...primaryButtonSx, height: 40, px: 2,
                "&.Mui-disabled": { bgcolor: COLORS.accent, color: COLORS.onAccent, opacity: 0.45 } }}>
              {busy ? "Opening sign-in…" : `Connect ${label}`}
            </Button>
          </Blockable>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      {missing.length > 0 && (
        <Box sx={{ display: "flex", gap: 1.5, p: "12px 14px", borderRadius: "12px",
          bgcolor: "rgba(244,183,64,0.08)", border: "1px solid rgba(244,183,64,0.40)" }}>
          <WarningAmberIcon sx={{ color: COLORS.amber, fontSize: 20, mt: "1px" }} />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Going live will fail</Typography>
            <Typography sx={{ mt: 0.4, fontSize: 13, lineHeight: 1.55, color: "#F2D9A6" }}>
              {label} did not grant {missing.join(" and ")}, which it needs to create a live video on a Page.
              Reconnect and accept every permission. If it still fails, the app has not passed Meta App
              Review for them yet, and only accounts listed as testers can go live meanwhile.
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.75 }}>
        <Box aria-hidden sx={{ width: 44, height: 44, borderRadius: "50%", flex: "none",
          bgcolor: tint.bg, color: tint.fg, fontWeight: 700, fontSize: 17,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          {(name || label).trim().charAt(0).toUpperCase()}
        </Box>
        <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>{name}</Typography>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flex: "none",
              px: 1, py: "3px", borderRadius: 99, fontSize: 12, fontWeight: 600,
              color: COLORS.green, bgcolor: "rgba(61,214,140,0.12)", border: "1px solid rgba(61,214,140,0.34)" }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLORS.green }} />
              Connected
            </Box>
          </Stack>
          <Typography sx={{ mt: 0.4, fontSize: 13, lineHeight: 1.5, color: COLORS.muted }}>
            Fennira creates the live {isYt ? "broadcast on this channel" : "video on this Page"} for you when you go live.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
        gap: 1.5, pt: 2, borderTop: `1px solid ${COLORS.border}` }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={!!conn.enabled}
            onChange={(e) => toggleConnection(platform, e.target.checked)}
            inputProps={{ "aria-labelledby": `${platform}-auto-label` }}
            sx={switchSx}
          />
          <Typography id={`${platform}-auto-label`} sx={{ fontSize: 14, color: COLORS.text }}>
            Go live here every time
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Blockable reason={blockedReason}>
            <Button onClick={() => connectPlatform(platform)} disabled={busy || !!blockedReason}
              sx={{ ...ghostButtonSx, ...smallBtn }}>
              {busy ? "Opening sign-in…" : `Switch ${thing}`}
            </Button>
          </Blockable>
          <Button onClick={() => setConfirmOff(true)} startIcon={<LinkOffIcon />}
            sx={{ ...ghostButtonSx, ...smallBtn, color: COLORS.red, borderColor: "rgba(229,72,77,0.45)",
              "&:hover": { bgcolor: COLORS.dangerSoft, borderColor: COLORS.red } }}>
            Disconnect
          </Button>
        </Stack>
      </Box>

      <Stack spacing={1.75} sx={{ pt: 2, borderTop: `1px solid ${COLORS.border}` }}>
        <Box>
          <Typography sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 16, color: COLORS.text }}>
            Next live session
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12, color: COLORS.muted }}>
            What viewers see on {label}. Saved as soon as you leave the field.
          </Typography>
        </Box>
        <Box>
          <Typography component="label" htmlFor={`${platform}-title`} sx={labelSx}>Title</Typography>
          <TextField
            {...commonInputProps}
            id={`${platform}-title`}
            key={`${platform}-title-${conn.title}`}
            placeholder={`What viewers see on ${label}`}
            defaultValue={conn.title || ""}
            inputProps={{ maxLength: 100 }}
            onBlur={(e) => saveMetadata(platform, "title", e.target.value)}
            sx={proseInputSx}
          />
        </Box>
        <Box>
          <Typography component="label" htmlFor={`${platform}-desc`} sx={labelSx}>
            Description <Box component="span" sx={{ fontWeight: 400, color: COLORS.muted }}>(optional)</Box>
          </Typography>
          <TextField
            {...commonInputProps}
            id={`${platform}-desc`}
            key={`${platform}-desc-${conn.description}`}
            placeholder="A line or two about the stream"
            defaultValue={conn.description || ""}
            multiline minRows={2} maxRows={5}
            inputProps={{ maxLength: 5000 }}
            onBlur={(e) => saveMetadata(platform, "description", e.target.value)}
            sx={proseInputSx}
          />
        </Box>
      </Stack>

      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        onConfirm={() => disconnectPlatform(platform)}
        icon={<LinkOffIcon />}
        title={`Disconnect ${name}?`}
        body={`Fennira will stop creating live ${isYt ? "broadcasts" : "videos"} on this ${thing}. Stream keys you added for ${label} keep working. You can connect again at any time.`}
        cancelLabel="Keep connected"
        confirmLabel="Disconnect"
      />
    </Box>
  );
}
